const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const COMPANY_STORAGE_KEY = 'company';

const LOGGED_OUT_AUTH_STATE = {
  loggedIn: false,
  currentUser: null,
  actingTenant: null,
  fortnoxConnected: false,
  crmEnabled: false,
};

function buildRequestError(message, status, data) {
  const error = new Error(message);
  error.status = status;
  error.data = data;
  return error;
}

async function parseResponseData(response) {
  const contentType = response.headers.get('content-type') || '';

  if (contentType.includes('application/json')) {
    return response.json();
  }

  const text = await response.text();
  return text ? { message: text } : null;
}

function normalizeAuthResponse(data) {
  if (!data?.authenticated || !data?.user) {
    return { ...LOGGED_OUT_AUTH_STATE };
  }

  return {
    loggedIn: true,
    currentUser: data.user ?? null,
    actingTenant: data.actingTenant ?? null,
    fortnoxConnected: Boolean(data.fortnoxConnected),
    crmEnabled: Boolean(data.crmEnabled),
  };
}

export function getLoggedOutAuthState() {
  return { ...LOGGED_OUT_AUTH_STATE };
}

export function getStoredCompany() {
  return localStorage.getItem(COMPANY_STORAGE_KEY) || '';
}

export function syncStoredCompany(company) {
  const trimmedCompany = company?.trim();

  if (trimmedCompany) {
    localStorage.setItem(COMPANY_STORAGE_KEY, trimmedCompany);
    return;
  }

  localStorage.removeItem(COMPANY_STORAGE_KEY);
}

export async function restoreAuthSession() {
  const response = await fetch(`${apiBaseUrl}/api/auth/me`, {
    credentials: 'include',
  });

  if (response.status === 401) {
    return getLoggedOutAuthState();
  }

  const data = await parseResponseData(response);

  if (!response.ok) {
    throw buildRequestError('Failed to restore session.', response.status, data);
  }

  return normalizeAuthResponse(data);
}

export async function loginUser({ username, password, company }) {
  const response = await fetch(`${apiBaseUrl}/api/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ username, password, company }),
  });

  const data = await parseResponseData(response);

  if (!response.ok || !data?.authenticated) {
    throw buildRequestError(data?.error || 'Login failed. Please try again.', response.status, data);
  }

  return normalizeAuthResponse(data);
}

async function updateActingTenant(tenantId) {
  const response = await fetch(`${apiBaseUrl}/api/auth/acting-tenant`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ tenantId }),
  });

  const data = await parseResponseData(response);

  if (!response.ok) {
    throw buildRequestError(data?.error || 'Failed to update tenant context.', response.status, data);
  }

  return normalizeAuthResponse(data);
}

export async function setActingTenant(tenantId) {
  return updateActingTenant(tenantId);
}

export async function clearActingTenant() {
  return updateActingTenant(null);
}

export async function fetchFortnoxConnectionStatus() {
  const response = await fetch(`${apiBaseUrl}/api/fortnox/connection-status`, {
    credentials: 'include',
  });

  if (response.status === 401) {
    return {
      authorized: false,
      connected: false,
      forbidden: false,
    };
  }

  if (response.status === 403) {
    return {
      authorized: true,
      connected: false,
      forbidden: true,
    };
  }

  const data = await parseResponseData(response);

  if (!response.ok) {
    throw buildRequestError('Failed to check connection status.', response.status, data);
  }

  return {
    authorized: true,
    connected: Boolean(data?.connected),
    forbidden: false,
  };
}

export async function logoutUser() {
  return fetch(`${apiBaseUrl}/api/auth/logout`, {
    method: 'POST',
    credentials: 'include',
  });
}

export function getFortnoxConnectionErrorMessage(errorCode) {
  if (errorCode === 'unauthorized') {
    return 'Failed to connect to Fortnox: Unauthorized. Please try again.';
  }

  return `Connection error: ${errorCode}`;
}

export async function startFortnoxLogin() {
  const response = await fetch(`${apiBaseUrl}/api/fortnox/login`, {
    credentials: 'include',
  });

  if (!response.ok) {
    const data = await parseResponseData(response);
    let message;

    if (response.status === 401) {
      message = 'Failed to connect to Fortnox: Unauthorized. Please try again.';
    } else if (response.status === 429) {
      message = 'Fortnox cannot handle requests at the moment. Please wait.';
    } else {
      message = data?.error || `Failed to initiate Fortnox login: ${response.status}.`;
    }

    throw buildRequestError(message, response.status, data);
  }

  return response.text();
}
